import { Navigate } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "../auth/useAuth";
import { LoadingScreen } from "./LoadingScreen";

interface RequireRoleProps {
  readonly roles: readonly string[];
  readonly children: ReactNode;
}

/**
 * Client-side role gating for navigation UX only - never the authoritative check. Every Manager
 * re-authorizes through IAuthorizationEngine server-side (EvergreenController.cs's own header:
 * "the authoritative, resource-aware decision is the Manager's"), so this component's only job is
 * to keep an obviously-unauthorized user from clicking into a screen that will just 403, not to
 * enforce anything a server refusal does not already enforce.
 */
export function RequireRole({ roles, children }: RequireRoleProps) {
  const { status, hasRole } = useAuth();

  if (status === "initializing") {
    return <LoadingScreen />;
  }

  if (status === "unauthenticated") {
    return <Navigate to="/" replace />;
  }

  if (!hasRole(...roles)) {
    return <Navigate to="/access-denied" replace />;
  }

  return <>{children}</>;
}
